import { computed } from '@preact/signals';
import { clamp, round } from 'es-toolkit';
import { TargetedPointerEvent } from 'preact';
import { forwardRef } from 'preact/compat';
import { useRef, useState } from 'preact/hooks';
import { pause, play } from '../../global-state/audio-player';
import {
  getCurrentEpisode,
  requestedState,
  state,
} from '../../global-state/audio-signals';
import { useUserDataStore } from '../../global-state/user-data';
import { readableDuration } from '../../utils/format-date';
import { classNames } from '../../utils/helpers';
import { Icon } from '../icon';

const isPlayingSignal = computed(
  () =>
    state.value === 'playing' ||
    (requestedState.value === 'playing' && !['error', 'unloaded'].includes(state.value))
);

const PlayerBar = forwardRef(
  (
    {
      isLogoVisible = true,
      expand,
      thumbUrl,
    }: { isLogoVisible: boolean; expand: () => void; thumbUrl: string },
    logoBoxRef: React.ForwardedRef<HTMLDivElement>
  ) => {
    const episode = getCurrentEpisode();
    const [secondsPlayed, secondsLeft] = useUserDataStore((s) =>
      episode ? s.episodesProgress[episode.id] : undefined
    ) || [NaN, NaN];

    const containerRef = useRef<HTMLDivElement>(null);
    const [dragState, setDragState] = useState<{ startY: number }>();
    const startDrag = (event) => {
      setDragState({ startY: event.pageY });
    };
    const handleDrag = (event) => {
      if (dragState && dragState.startY - event.pageY > 20) {
        expand();
        setDragState(undefined);
      }
    };
    const endDrag = () => setDragState(undefined);
    const logoBoxStyles = [
      isLogoVisible ? '' : 'visibility: hidden',
      thumbUrl ? `background-image: url(${thumbUrl})` : 'width: 0; border: 0;',
    ]
      .filter(Boolean)
      .join(';');

    return (
      <>
        {/* Blur background behind & below player bar */}
        <div
          class={classNames(
            'fixed bottom-[var(--bottom-nav-height)] left-0 h-[calc(var(--player-bar-height)/2+4px)] w-full',
            'bg-white/80 backdrop-blur',
            !episode && 'hidden'
          )}
        ></div>
        {/* Floating player bar */}
        <div
          class={classNames(
            'fixed bottom-[calc(var(--bottom-nav-height)+4px)] left-1 h-[var(--player-bar-height)] w-[calc(100%-8px)]',
            'sm:bottom-[calc(var(--bottom-nav-height)+8px)] sm:left-2 sm:w-[calc(100%-16px)] md:w-[calc(100%-16px)]',
            'flex touch-none items-center overflow-clip rounded-lg shadow-lg',
            'border border-slate-700/20',
            !episode && 'hidden'
          )}
          style="background-color: rgba(255, 255, 255, 0.9); backdrop-filter: blur(8px);"
          onClick={(event: TargetedPointerEvent<HTMLDivElement>) => {
            if (event.target instanceof Element && event.target.nodeName !== 'BUTTON') {
              // Ignore clicks on buttons in nav bar
              expand();
            }
          }}
          onPointerDown={startDrag}
          onPointerMove={handleDrag}
          onPointerCancel={endDrag}
          onPointerUp={endDrag}
          ref={containerRef}
        >
          <div
            ref={logoBoxRef}
            class="m-2 h-8 w-8 shrink-0 rounded-sm bg-cover bg-center"
            style={logoBoxStyles}
          ></div>
          <div class="leading-[1.2 grid max-h-9 flex-1 grow grid-cols-[1fr_auto] items-center">
            <span className="line-clamp-2 min-w-0 overflow-hidden text-ellipsis leading-[1.2]">
              {episode?.title || ''}
            </span>
            <span class="ml-1 whitespace-nowrap text-sm leading-9 text-slate-600">
              {getTimeLeftText()}
            </span>
          </div>
          {isPlayingSignal.value ? (
            <button
              class="flex h-12 w-12 shrink-0 items-center justify-center p-2 pl-1"
              onClick={() => pause()}
              disabled={!thumbUrl}
            >
              <Icon sprite="pause" class="h-6 w-6" />
            </button>
          ) : (
            <button
              class="flex h-12 w-12 shrink-0 items-center justify-center p-2 pl-1"
              onClick={() => play()}
              disabled={!thumbUrl}
            >
              <Icon sprite="play" class="h-6 w-6" />
            </button>
          )}
          <div
            class={classNames('absolute bottom-0 h-[2px] bg-icon-blue')}
            style={{ width: `${getPercentPlayed()}%` }}
          ></div>
        </div>
      </>
    );

    function getPercentPlayed() {
      if (!episode || !secondsPlayed || !episode.duration) return 0;
      return clamp(round((100 * secondsPlayed) / episode.duration, 1), 0, 100);
    }

    function getTimeLeftText() {
      if (!episode || !episode.duration) return '';

      if (secondsPlayed > 0 && secondsLeft > 0)
        return `${readableDuration(secondsLeft, '')} left`;

      return `${readableDuration(episode.duration, '')}`;
    }
  }
);

export default PlayerBar;
